import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";

import {
  Image,
  Card,
  CardHeader,
  CardBody,
  Button,
  useDisclosure,
} from "@nextui-org/react";
import MarkdownWithInputs from "../../components/Policies/MarkdownwithInputs";
import { editIcon } from "../../utils/constant";
import EditPolicyFrameWorkModal from "./EditPolicyFrameWorkModal";
import {
  capitalize,
  getDataWithFilterUrl,
  separateDasFromUrl,
  UpdateOneData,
} from "../../controllers/APIController";
import LoadingPage from "../../components/Loader/LoadingPage";
import { getImageForDomain } from "../../constant/Policies/policyConstant";
import ProcedureCard from "../../components/Policies/ProcedureCard";
import { StandardDeleteButton } from "../../components/StandardButtons";

/**
 * Extract procedure fields from markdown text using handlebar syntax
 * Finds all {{fieldName}} patterns and creates an object with empty values
 * @param {string} text - Markdown text containing handlebar placeholders
 * @returns {Object} Object with field names as keys and empty strings as values
 */
const getAllProcedureFields = (text) => {
  let data = {};
  const handlebarRegex = /{{([^}]+)}}/g;
  let match;
  
  while ((match = handlebarRegex.exec(text)) !== null) {
    const fieldName = match[1].trim();
    data = { ...data, [fieldName]: "" };
  }
  
  return data;
};

/**
 * SinglePolicy Component
 * Displays and manages a single policy with its procedures
 * Allows editing policy content and viewing associated procedures
 * @returns {JSX.Element} Single policy detail view
 */
const SinglePolicy = () => {
  // URL parameters for routing
  const { _policy, _orgId: currentOrgId } = useParams();
  const policyName = _policy ? _policy.replace(/-/g, " ") : "";
  
  // User and organization information from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  // Policy title and filter URL
  const headingTitle = capitalize(separateDasFromUrl(policyName));
  const filteredUrl = orgFilterUrl + "&filters[Name][$eqi]=" + headingTitle;

  // Component state
  const [isLoading, setIsLoading] = useState(true);
  const [markdownPolicyData, setMarkdownPolicyData] = useState({});
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [reRenderPage, setReRenderPage] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});
  const [policy, setPolicy] = useState(null);
  const [procedures, setProcedures] = useState([]);

  /**
   * Validate that all required fields are filled
   * Checks if any procedure fields are empty and sets error messages
   * @returns {boolean} True if validation fails, false if passes
   */
  const validateForm = () => {
    let hasErrors = false;
    const errors = {};
    
    for (let key in markdownPolicyData) {
      if (markdownPolicyData[key] === "") {
        hasErrors = true;
        errors[key] = "This field is required!";
      }
    }
    
    if (hasErrors) setErrorMessage(errors);
    return hasErrors;
  };

  /**
   * Handle form submission to update policy
   * Validates form data and updates the policy in the backend
   */
  const submitHandler = async () => {
    if (validateForm()) return;

    const updatedPolicyResponseData = {
      Response: { ...markdownPolicyData },
    };

    await UpdateOneData("policies", policy?.id, updatedPolicyResponseData);
    window.location.reload();
  };

  /**
   * Reset policy data to original values
   * Fetches the original policy and resets all changes
   */
  const resetPolicyData = async () => {
    const policyOriginId = policy?.attributes?.OriginId;
    const filteredUrl = `filters[id][$eqi]=${policyOriginId}`;
    const response = await getDataWithFilterUrl("policies", filteredUrl);
    const originPolicyInfo = response?.data[0]?.attributes;

    const data = {
      PolicyFramework: originPolicyInfo?.PolicyFramework,
      Response: null,
    };
    await UpdateOneData("policies", policy?.id, data);
    window.location.reload();
  };

  /**
   * Fetch policy data and procedures on component mount
   * Loads policy details and extracts procedure information
   */
  useEffect(() => {
    getDataWithFilterUrl("policies", filteredUrl)
      .then((data) => {
        if (data.data && data.data.length > 0) {
          const policyData = data.data[0];
          setPolicy(policyData);
          
          // Extract procedures from the policy response
          const proceduresData = policyData?.attributes?.procedures?.data || [];
          setProcedures(proceduresData);
          
          // Set policy response data or extract from framework
          const policyResponse =
            policyData?.attributes?.Response ||
            getAllProcedureFields(policyData?.attributes?.PolicyFramework);
          setMarkdownPolicyData(policyResponse);
        } else {
          setPolicy(null);
          setProcedures([]);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching policy data:", error);
        setIsLoading(false);
      });
  }, [filteredUrl, reRenderPage]);

  return (
    <BreadCrumbsPage>
      <LoadingPage isLoading={isLoading}>
        <div>
          {/* Policy Header */}
          {policy && (
            <div className="flex justify-between">
              <div className="flex gap-4 items-center">
                <Image
                  alt="Policy domain logo"
                  height={40}
                  radius="sm"
                  src={getImageForDomain(
                    policy?.attributes?.DomainId?.data?.attributes?.Name
                  )}
                  width={40}
                />
                <p className="text-3xl font-medium">{policyName}</p>
              </div>
              <StandardDeleteButton onClick={submitHandler} aria-label="Update policy">
                Update Policy
              </StandardDeleteButton>
            </div>
          )}
          
          {/* Policy Description */}
          <div className="mt-8">{policy && policy.attributes.Description}</div>
          
          {/* Edit Policy Modal */}
          <EditPolicyFrameWorkModal
            onReRender={setReRenderPage}
            onSubmitHandler={1}
            id={policy?.id}
            modalHeading={"Are you sure you want to delete this Entry?"}
            isOpen={isOpen}
            onOpen={onOpen}
            onOpenChange={onOpenChange}
          />
          
          {/* Main Content Layout */}
          <div className="flex items-start gap-8 mt-8">
            {/* Policy Content Section */}
            <div className="w-[65%]">
              <Card
                key={policy?.id}
                className="px-4 py-2 border border-divider"
                shadow="none"
              >
                {/* Card Header with Action Buttons */}
                <CardHeader className="flex items-center justify-end gap-2">
                  <Button
                    color="danger"
                    variant="ghost"
                    onClick={onOpen}
                    className="flex items-center justify-center"
                    aria-label="Edit policy"
                  >
                    {editIcon}
                    <span>Edit</span>
                  </Button>
                  <Button
                    color="secondary"
                    variant="ghost"
                    onClick={resetPolicyData}
                    className="flex items-center justify-center"
                    aria-label="Reset policy data"
                  >
                    <span>Reset</span>
                  </Button>
                </CardHeader>
                
                {/* Card Body with Markdown Editor */}
                <CardBody>
                  <MarkdownWithInputs
                    markdownContent={policy?.attributes?.PolicyFramework}
                    procedureData={markdownPolicyData}
                    onChangeProcedureData={setMarkdownPolicyData}
                    errorMessage={errorMessage}
                  />
                </CardBody>
              </Card>
            </div>
            
            {/* Procedures Sidebar */}
            <div className="w-[30%] max-h-[500px] overflow-auto hide-scrollbar">
              {procedures.length > 0 && (
                <>
                  <p className="text-xl font-medium mb-4">Procedures</p>
                  <div className="flex flex-col gap-4">
                    {procedures?.map((procedure) => (
                      <ProcedureCard
                        currOrg={currentOrgId}
                        _policy={_policy}
                        procedure={procedure}
                        key={procedure?.id}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </LoadingPage>
    </BreadCrumbsPage>
  );
};

export default SinglePolicy;
